import { apiClient } from '@/lib/api/client';
import type { InternalAxiosRequestConfig } from 'axios';
import type { 
  SlotAvailabilityResponse, 
  SyncTableBookingCartItemsRequestBody,
} from '@/features/booking/types/tableBookingTicketTypes';
import type { CartResponse } from '@/features/cart/types';

/**
 * Service for table booking ticket operations (EBC-level)
 */
export const tableBookingTicketService = {
  /**
   * Fetches available table slot indices for a single event booking category.
   * Each slot index corresponds to a time window: schedule.startTime + slotIndex * customSlotDuration.
   * @param eventBookingCategoryID - The ID of the event booking category
   * @returns Promise containing a map of table numbers to available slot indices
   */
  getTableAvailability: async (
    eventBookingCategoryID: string
  ): Promise<Record<string, number[]>> => {
    try {
      const response = await apiClient.get<SlotAvailabilityResponse>(
        `/api/booking/v1/availability/table/event-booking-category/${eventBookingCategoryID}/slots`
      );
      
      // Response structure: { data: { [tableNumber]: number[] (slot indices) } }
      return response.data || {};
    } catch (error) {
      console.error('Error fetching table availability:', error);
      throw new Error('Failed to fetch table availability');
    }
  },

  /**
   * Atomically synchronizes (adds, updates, and removes) multiple cart items for a table booking EBC
   * @param eventBookingCategoryID - The ID of the event booking category
   * @param body - The sync request body containing is_guest_cart and selected_items
   * @returns Promise containing the updated cart response
   */
  syncTableBookingCartItems: async (
    eventBookingCategoryID: string,
    body: SyncTableBookingCartItemsRequestBody
  ): Promise<CartResponse> => {
    try {
      console.log('Sync Table Booking Cart Items Request:', JSON.stringify(body, null, 2));
      
      const config: InternalAxiosRequestConfig = { timeout: 30000 } as InternalAxiosRequestConfig;
      
      const response = await apiClient.put<{ data: CartResponse }>(
        `/api/booking/v1/cart/event-booking-category/${eventBookingCategoryID}/sync-table-booking-cart-items`,
        body,
        config // 30 seconds timeout for sync operation
      );
      
      return response.data;
    } catch (error) {
      console.error('Error syncing table booking cart items:', error);
      // Re-throw the original error if it's already an Error instance
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to sync table booking cart items');
    }
  },
};

